import { useState, useCallback } from 'react';
import {
  Heart, Sparkles, Music, MessageCircle, Star,
  Sun, Zap, Shield, Infinity,
} from 'lucide-react';
import { REASONS, REASONS_SECTION, HER_NAME } from '@/config/valentineContent';

const ICON_MAP = { heart: Heart, sparkles: Sparkles, music: Music, message: MessageCircle, star: Star, sun: Sun, zap: Zap, shield: Shield, infinity: Infinity };
const ACCENT_CYCLE = ['#E91E63', '#EC407A', '#F06292', '#E91E63', '#D81B60', '#EC407A', '#F06292', '#E91E63', '#D81B60'];

const reasons = REASONS.map((r, i) => ({
  Icon: ICON_MAP[r.icon] || Heart,
  text: r.text,
  accent: ACCENT_CYCLE[i % ACCENT_CYCLE.length],
  featured: r.featured || false,
}));

const ReasonCard = ({ reason, index, revealed, onReveal }) => {
  const { Icon, text, featured } = reason;
  const num = String(index + 1).padStart(2, '0');

  return (
    <div
      data-testid={`reason-card-${index}`}
      onClick={() => onReveal(index)}
      className={`reason-card-container cursor-pointer ${featured ? 'reason-featured' : ''}`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className={`reason-card-flipper ${revealed ? 'reason-flipped' : ''}`}>
        {/* FRONT — sealed envelope */}
        <div
          className="reason-card-face reason-card-front"
          style={{ backgroundColor: 'var(--bg-card)' }}
        >
          <div className="absolute inset-0 reason-card-pattern" />
          <div className="relative z-10 flex flex-col items-center justify-center h-full gap-3">
            <span
              className="font-heading text-4xl md:text-5xl opacity-20"
              style={{ color: 'var(--accent-primary)' }}
            >
              {num}
            </span>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center reason-seal"
              style={{ backgroundColor: 'var(--accent-glow)' }}
            >
              <Heart size={14} fill="var(--accent-primary)" strokeWidth={0} />
            </div>
            <span
              className="font-body text-[10px] tracking-widest uppercase"
              style={{ color: 'var(--accent-light)' }}
            >
              Tap to reveal
            </span>
          </div>
        </div>

        {/* BACK — revealed reason */}
        <div
          className="reason-card-face reason-card-back"
          style={{ backgroundColor: 'var(--bg-card)' }}
        >
          <div
            className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
            style={{ background: `linear-gradient(90deg, transparent, var(--accent-primary), transparent)` }}
          />
          <div className="relative z-10 flex flex-col items-center justify-center h-full gap-4 p-5 text-center">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center reason-icon-glow"
              style={{
                background: `linear-gradient(135deg, var(--accent-glow), var(--accent-glow))`,
                boxShadow: `0 0 20px var(--accent-glow)`,
              }}
            >
              <Icon size={22} style={{ color: 'var(--accent-primary)' }} />
            </div>
            <p
              className="font-body text-sm leading-relaxed"
              style={{ color: 'var(--text-secondary)' }}
            >
              {text}
            </p>
            <span
              className="font-heading text-xs opacity-25"
              style={{ color: 'var(--accent-primary)' }}
            >
              #{num}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ReasonsGrid() {
  const [revealed, setRevealed] = useState({});
  const revealedCount = Object.values(revealed).filter(Boolean).length;
  const allRevealed = revealedCount === reasons.length;

  const revealOne = useCallback((i) => {
    setRevealed(prev => ({ ...prev, [i]: true }));
  }, []);

  const revealAll = () => {
    reasons.forEach((_, i) => {
      setTimeout(() => revealOne(i), i * 100);
    });
  };

  return (
    <section id="reasons" data-testid="reasons-section" className="section-romantic">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <p
            className="font-body text-[10px] tracking-widest uppercase mb-2"
            style={{ color: 'var(--accent-light)' }}
          >
            A collection of
          </p>
          <h2
            className="font-heading text-2xl md:text-3xl mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Reasons I Love You
          </h2>
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="reason-header-line" />
            <Heart size={12} fill="var(--accent-primary)" strokeWidth={0} />
            <div className="reason-header-line" />
          </div>
        </div>

        {/* Progress counter */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="flex gap-1">
            {reasons.map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full transition-all duration-500"
                style={{
                  backgroundColor: revealed[i] ? 'var(--accent-primary)' : 'var(--accent-glow)',
                  transform: revealed[i] ? 'scale(1.2)' : 'scale(1)',
                }}
              />
            ))}
          </div>
          <span className="font-body text-[10px]" style={{ color: 'var(--text-secondary)' }}>
            {revealedCount} of {reasons.length}
          </span>
        </div>

        {/* Bento grid */}
        <div className="reason-bento-grid">
          {reasons.map((reason, i) => (
            <ReasonCard
              key={i}
              reason={reason}
              index={i}
              revealed={!!revealed[i]}
              onReveal={revealOne}
            />
          ))}
        </div>

        {/* Reveal All button */}
        {!allRevealed && (
          <div className="text-center mt-8">
            <button
              data-testid="reveal-all-reasons-btn"
              onClick={revealAll}
              className="px-6 py-2 rounded-xl font-body text-xs transition-all duration-300 hover:scale-105"
              style={{
                color: 'var(--accent-primary)',
                border: '1px solid var(--accent-light)',
              }}
            >
              Reveal all reasons
            </button>
          </div>
        )}

        {/* All revealed celebration */}
        {allRevealed && (
          <div className="text-center mt-8 reason-all-done">
            <p className="font-cursive text-xl md:text-2xl" style={{ color: 'var(--accent-primary)' }}>
              {REASONS_SECTION.allRevealedText}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
